import React, { useMemo } from "react";
import {
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { handleUuid } from "../utils/utils";
import { shouldBeLocked } from "../utils/Roles";
import { ID } from "../types";

const useStyles = makeStyles({
  label: {
    fontSize: "1.25rem",
    position: "relative",
    paddingRight: "25px",
    "&div": {
      marginTop: 0,
      "&:before": {
        bottom: "5px",
      },
      "&:after": {
        bottom: "5px",
      },
      svg: {
        top: "0",
      },
    },
  },
  disabled: {
    "&:hover": {
      cursor: "not-allowed",
    },
  },
});
interface Option {
  id?: ID;
  name?: string;
  position?: number;
  [key: string]: any;
}

interface BaseDropDownProps {
  id?: string;
  value?: Option | "";
  required?: boolean;
  options?: Option[] | string[];
  title?: string;
  bgColor?: string;
  bRadius?: string;
  classStyle?: string;
  selectStyle?: string;
  inputLabel?: React.RefObject<HTMLLabelElement> | null;
  labelWidth?: number;
  labelStyle?: React.CSSProperties;
  stateName?: string;
  name?: string;
  nameFormatter?: (option: Option) => string;
  helperTextStyle?: string;
  helperText?: string;
  userRoles?: any;
  autoFocus?: boolean;
  canEdit?: boolean;
  showBlank?: boolean;
  error?: boolean;
}
interface DisabledDropDownProps extends BaseDropDownProps {
  disabled: true;
  handleChange: (
    event: React.ChangeEvent<{ name?: string; value: ID }>
  ) => void;
}

interface NotDisabledDropDownProps extends BaseDropDownProps {
  disabled?: false;
  handleChange?: (
    event: React.ChangeEvent<{ name?: string; value: ID }>
  ) => void;
}

type SingleDropDownProps = DisabledDropDownProps | NotDisabledDropDownProps;

type ParsedValue = any;

type CustomChangeEvent = React.ChangeEvent<{
  name?: string;
  value: ParsedValue;
}>;

//Refactored to not use the labelWidth and inputLabel as they were not working. Test for a while with this change before removing all the inputLabels and labelWidths
const SingleDropDown: React.FC<SingleDropDownProps> = ({
  id = "",
  value = "",
  required = false,
  options,
  title = "",
  bgColor,
  bRadius,
  classStyle,
  selectStyle,
  inputLabel,
  labelWidth,
  labelStyle,
  stateName,
  handleChange,
  disabled = undefined,
  name = "name",
  nameFormatter,
  helperTextStyle,
  helperText,
  userRoles,
  autoFocus = false,
  showBlank = true,
  error = false,
  ...props
}) => {
  const classes = useStyles();
  const memoizedLocked = useMemo(
    () => shouldBeLocked(disabled, props?.canEdit, userRoles),
    [disabled, props?.canEdit, userRoles]
  );
  const sortedOptions = useMemo(() => {
    //If there's nothing or an empty array, return an empty array
    if (!options || options.length === 0) return [];

    // Check if the options are of string type
    if (typeof options[0] === "string") return options as string[];

    //Otherwise the option will be type Option
    return [...(options as Option[])].sort((a, b) => {
      if (a?.position === undefined && b?.position === undefined) return 0;
      if (a?.position === undefined) return 1;
      if (b?.position === undefined) return -1;
      return a?.position - b?.position;
    });
  }, [options]);

  const formatTitleToId = title.toLowerCase().replace(/\s+/g, "-");

  return (
    <FormControl margin="dense" variant="standard" className={classStyle}>
      <InputLabel
        margin="dense"
        shrink={true}
        className={classes.label}
        style={labelStyle}
        data-cy={title + "-label"}
        ref={inputLabel}
        id={`${formatTitleToId}-label`}
      >
        {title}
      </InputLabel>
      {/* eslint-disable-next-line jsx-a11y/no-autofocus -- Preserve focus requested by forms when an invalid selection needs attention. */}
      <Select autoFocus={autoFocus}
        // disabled={disabled || !props?.canEdit}
        error={error}
        required={required}
        disabled={memoizedLocked}
        inputProps={memoizedLocked ? { className: classes.disabled } : {}}
        data-cy={title + "-select"}
        labelId={`${formatTitleToId}-label`}
        id={id}
        data-testid={id}
        name={stateName}
        defaultValue={value || ""}
        className={selectStyle}
        value={
          value === null || value === undefined || !options?.length
            ? ""
            : typeof options[0] === "string"
            ? options[options.findIndex(option => option === value)]
            : JSON.stringify(
                options.find(
                  option =>
                    typeof option !== "string" &&
                    typeof value === "object" &&
                    option &&
                    String(option?.id) === String(value?.id)
                )
              ) ?? ""
        }
        native={false}
        renderValue={() => {
          if (typeof value === "string") {
            return value;
          }
          if (!nameFormatter) {
            return value[name];
          }
          return nameFormatter(value);
        }}
        onChange={(originalEvent: React.ChangeEvent<{ value: unknown }>) => {
          // handle ""
          const parsedValue: ParsedValue =
            originalEvent.target.value === ""
              ? ""
              : JSON?.parse(originalEvent.target.value as string);

          // Create a new event object with the parsed value and original event's properties
          const newEvent: CustomChangeEvent = {
            ...originalEvent,
            target: {
              ...originalEvent.target,
              value: parsedValue,
            },
          } as CustomChangeEvent;

          originalEvent?.stopPropagation();

          if (handleChange) handleChange(newEvent);
        }}
        style={{
          marginTop: "0px",
          cursor: memoizedLocked ? "not-allowed" : "",
          backgroundColor: bgColor ? bgColor : "",
          borderRadius: bRadius ? bRadius : "",
        }}
        // labelWidth={labelWidth}
      >
        {showBlank && (
          <MenuItem
            data-cy={title + "-blank-dropdown-item"}
            key={`no-${title}-type`}
            value={JSON.stringify({ id: "", name: "" })}
          >
            <span style={{ visibility: "hidden" }}>Clear Input</span>
          </MenuItem>
        )}
        {sortedOptions.map((option, index) => (
          <MenuItem
            data-cy={title + "-dropdown-item-" + index}
            key={handleUuid()}
            value={JSON.stringify(option)}
          >
            {typeof option === "string"
              ? option
              : !nameFormatter
              ? option[name as keyof Option]
              : nameFormatter(option)}
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <FormHelperText className={helperTextStyle}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default SingleDropDown;
